import type {
  BlockedRange,
  Nullable,
  RvExpandedDetails,
} from '~/types'

/**
 * Blocks every dates after the selling date.
 *
 * @param rvRVSellingDate The selling date to block.
 * @param timezone The timezone to use.
 * @returns The blocked ranges.
 */
export function getRvCalendarBlockedRangesFromSellingDate(
  rvRVSellingDate: Nullable<RvExpandedDetails['RVSellingDate']>,
  timezone: string,
): BlockedRange[] {
  if (!rvRVSellingDate) {
    return []
  }

  const sellingDate = datetime.tz(rvRVSellingDate, timezone)
  return [{ start: sellingDate.toDate(), end: null }]
}

/**
 * Block ranges outside of Booking season.
 *
 * @param rvBookingSeasonStart The start of the booking season.
 * @param rvBookingSeasonEnd The end of the booking season.
 * @param timezone The timezone to use.
 * @returns The blocked ranges.
 */
export function getRvCalendarBlockedRangesFromBookingSeason(
  rvBookingSeasonStart: Nullable<RvExpandedDetails['BookingSeasonStart']>,
  rvBookingSeasonEnd: Nullable<RvExpandedDetails['BookingSeasonEnd']>,
  timezone: string,
): BlockedRange[] {
  const blockedRanges: BlockedRange[] = []

  if (!rvBookingSeasonStart || !rvBookingSeasonEnd) {
    return blockedRanges
  }

  const isEndBeforeStart = rvBookingSeasonEnd < rvBookingSeasonStart

  // Start the date range from the end of the season
  const blockStart = rvBookingSeasonEnd.toString()
  const blockStartDay = blockStart.slice(-2)
  const blockStartMonth = blockStart.slice(0, -2)
  const blockStartYear = datetime.utc().tz(timezone).year() - (isEndBeforeStart ? 0 : 1)

  // End the date range before the start of the season
  const blockEnd = rvBookingSeasonStart.toString()
  const blockEndDay = blockEnd.slice(-2)
  const blockEndMonth = blockEnd.slice(0, -2)
  const blockEndYear = datetime.utc().tz(timezone).year()

  /**
   * We support 2 years in the future
   */
  for (let i = 0; i < 3; i++) {
    blockedRanges.push({
      start: datetime.tz(`${blockStartYear + i}-${blockStartMonth}-${blockStartDay}`, 'YYYY-M-DD', timezone)
        .add(1, 'day')
        .toDate(),
      end: datetime.tz(`${blockEndYear + i}-${blockEndMonth}-${blockEndDay}`, 'YYYY-M-DD', timezone)
        .subtract(1, 'day')
        .toDate(),
    })
  }

  return blockedRanges
}

/**
 * Block ranges from existing bookings and blocked dates.
 *
 * When prep time immediately precedes a booking, treat prep + booking as one
 * block so the booking start day is not available. All this is very much
 * business logic, and ideally we should move all of this to the back-end when
 * we redo calendars.
 *
 * @param rvCalendars The calendars to block.
 * @param timezone The timezone to use.
 * @returns The blocked ranges.
 */
export function getRvCalendarBlockedDates(rvCalendars: Nullable<RvExpandedDetails['Calendars']>, timezone: string): BlockedRange[] {
  const blockedRanges: BlockedRange[] = []
  const calendarList = rvCalendars ?? []

  calendarList.forEach((range) => {
    const startDate = datetime.tz(range.StartDate, timezone).add(12, 'hours')
    const endDate = datetime.tz(range.EndDate, timezone).add(12, 'hours')

    // If startDate is the same as the endDate, it's a single blocked date (e.g. manual block, prep day)
    if (endDate.diff(startDate, 'days') === 0) {
      blockedRanges.push(startDate.toDate())
    }
    else {
      // Multi-day = booking. Block from (prep start or booking start+1) through end-1. Checkout day (end) stays available.
      const blockedEndDate = endDate.subtract(1, 'day')
      const dayBeforeBookingStart = startDate.clone().subtract(1, 'day').startOf('day')

      // Only merge when the preceding entry is prep time (not another booking); avoids merging back-to-back bookings.
      const precedingRanges = calendarList.filter(
        (r) =>
          r !== range
          && (r as { Reason?: string }).Reason === 'PreparationTime'
          && datetime.tz(r.EndDate, timezone).add(12, 'hours').isSame(dayBeforeBookingStart, 'day'),
      )

      let blockedStartDate
      if (precedingRanges.length > 0) {
        // Merge: block from earliest preceding StartDate through last night of booking
        const earliestStart = precedingRanges.reduce((earliest, r) => {
          const rStart = datetime.tz(r.StartDate, timezone).add(12, 'hours')
          return !earliest || rStart.isBefore(earliest) ? rStart : earliest
        }, null as Nullable<ReturnType<typeof datetime>>)
        blockedStartDate = earliestStart ?? startDate.add(1, 'day')
      }
      else {
        blockedStartDate = startDate.add(1, 'day')
      }

      if (blockedEndDate.isBefore(blockedStartDate)) {
        return
      }

      blockedRanges.push({
        start: blockedStartDate.toDate(),
        end: blockedEndDate.toDate(),
      })
    }
  })

  return blockedRanges
}
